import { intl } from "LocaleWrapper";
import { Box, Divider, Grid2 as Grid, Stack, Typography, alpha } from "@mui/material";
import { Language } from "imports/Utils";
import { CallType } from "__generated__/enums";
import { GeneralCallFragment } from "__generated__/view/calls/graphql/call.fragments.generated";

type CallContainersAmountProps = {
    call: Pick<
        GeneralCallFragment,
        "numberOfLoadingContainers" |
        "numberOfDischargingContainers" |
        "numberOfLoadingReeferContainers" |
        "numberOfDischargingReeferContainers" |
        "loadedWeight" |
        "dischargedWeight"
    >;
    loadingOrDischarging: CallType;
    compact?: boolean;
};

const ROWS_CONFIG = [
	{ id: "CALL_CONTAINER_AMOUNT_LESS_THAN_OR_EQUAL_TO_20FT", pfx: "LessThanOrEqualTo20ft" },
	{ id: "CALL_CONTAINER_AMOUNT_FROM_21FT_THROUGH_30FT", pfx: "From21ftThrough30ft" },
	{ id: "CALL_CONTAINER_AMOUNT_FROM_31FT_THROUGH_40FT", pfx: "From31ftThrough40ft" },
	{ id: "CALL_CONTAINER_AMOUNT_GREATER_THAN_4OFT", pfx: "GreaterThan40ft" },
] as const;

const CallContainersAmount = ({
	call,
	loadingOrDischarging,
	compact = false,
}: CallContainersAmountProps) => {

	const isLoading = loadingOrDischarging === CallType.Loading;
	const containers = isLoading ? call.numberOfLoadingContainers : call.numberOfDischargingContainers;
	const reefers = isLoading ? call.numberOfLoadingReeferContainers : call.numberOfDischargingReeferContainers;
	const weight = (isLoading ? call.loadedWeight : call.dischargedWeight) ?? 0;

	return (
		<Box
			sx={{
				backgroundColor: "background.paper",
				border: "1px solid",
				borderColor: (theme) => alpha(theme.palette.primary.main, 0.08),
				overflow: "hidden",
				width: "100%",
			}}>
			<Stack divider={<Divider sx={{ borderColor: (theme) => alpha(theme.palette.primary.main, 0.04) }} />}>
				<MatrixHeader compact={compact} />

				{ROWS_CONFIG.map((row) => (
					<MatrixRow
						key={row.id}
						title={intl.formatMessage({ id: row.id })}
						full={{
							reefer: reefers?.[`numFull${row.pfx}`] ?? 0,
							nonReefer: containers?.[`numFull${row.pfx}`] ?? 0,
						}}
						empty={{
							reefer: reefers?.[`numEmpty${row.pfx}`] ?? 0,
							nonReefer: containers?.[`numEmpty${row.pfx}`] ?? 0,
						}}
						total={{
							reefer: reefers?.[`num${row.pfx}`] ?? 0,
							nonReefer: containers?.[`num${row.pfx}`] ?? 0,
						}}
						compact={compact} />
				))}

				{!compact && (
					<MatrixRow
						title={intl.formatMessage({ id: "AMOUNT" })}
						full={{
							reefer: reefers?.numTotalFullContainers ?? 0,
							nonReefer: containers?.numTotalFullContainers ?? 0,
						}}
						empty={{
							reefer: reefers?.numTotalEmptyContainers ?? 0,
							nonReefer: containers?.numTotalEmptyContainers ?? 0,
						}}
						total={{
							reefer: reefers?.numTotalContainers ?? 0,
							nonReefer: containers?.numTotalContainers ?? 0,
						}}
						compact={compact}
						isTotalRow />
				)}

				{!compact && (
					<Box
						sx={{
							backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.02),
							px: 2,
							py: 1,
							display: "flex",
							gap: 4,
						}}>
						<MatrixFooterRow
							title={intl.formatMessage({ id: "TEU" })}
							amount={(containers?.totalTeu ?? 0) + (reefers?.totalTeu ?? 0)} />

						<MatrixFooterRow
							title={intl.formatMessage({ id: "WEIGHT" })}
							amount={weight.toFixed(2)} />
					</Box>
				)}
			</Stack>
		</Box>
	);
};

const MatrixHeader = ({ compact }: { compact: boolean }) => {
	return (
		<Grid
			sx={{
				backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.03),
				py: 1,
				px: 2,
				alignItems: "center",
			}}
			container
			spacing={0.5}>
			<Grid size={compact ? 4 : 3.5} />

			{[ "FULL", "EMPTY" ].map((status) => (
				<Grid size={compact ? 4 : 3.25} key={status}>
					<Stack spacing={0.5}>
						<Typography
							variant="caption"
							sx={{
								fontSize: 10,
								fontWeight: 700,
								textAlign: "center",
								letterSpacing: "0.8px",
								textTransform: "uppercase",
							}}>
							{Language(status)}
						</Typography>

						<Grid container>
							{[ "CARGO_R", "CARGO_NON_R" ].map((it) => (
								<Grid size={6} sx={{ display: "flex", justifyContent: "center" }} key={it}>
									<Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", fontSize: 9.5 }}>
										{Language(it)}
									</Typography>
								</Grid>
							))}
						</Grid>
					</Stack>
				</Grid>
			))}

			{!compact && (
				<Grid size={2}>
					<Typography
						variant="caption"
						sx={{
							color: "text.primary",
							fontSize: 10,
							fontWeight: 700,
							textAlign: "center",
							display: "block",
							letterSpacing: "0.8px",
							textTransform: "uppercase",
						}}>
						{Language("TOTAL")}
					</Typography>
				</Grid>
			)}
		</Grid>
	);
};

type MatrixRowAmounts = {
    nonReefer: number;
    reefer: number;
};

const MatrixRow = ({
	title,
	full,
	empty,
	total,
	compact,
	isTotalRow = false,
}: {
    title: string;
    full: MatrixRowAmounts;
    empty: MatrixRowAmounts;
    total: MatrixRowAmounts;
    compact: boolean;
    isTotalRow?: boolean;
}) => {
	const totalReeferAndNonReefer = total.reefer + total.nonReefer;

	return (
		<Grid
			container
			spacing={0.5}
			sx={{
				py: isTotalRow ? 1.25 : 1,
				px: 2,
				alignItems: "center",
				backgroundColor: isTotalRow ? (theme) => alpha(theme.palette.primary.main, 0.02) : "transparent",
			}}>
			<Grid size={compact ? 4 : 3.5}>
				<Typography
					variant="body2"
					sx={{
						color: isTotalRow ? "text.primary" : "text.secondary",
						fontSize: 11,
						fontWeight: isTotalRow ? 700 : 500,
						lineHeight: 1.2,
					}}>
					{title}
				</Typography>
			</Grid>

			{[ full, empty ].map((amount, index) => (
				<Grid size={compact ? 4 : 3.25} key={index}>
					<Grid container>
						{[ amount.reefer, amount.nonReefer ].map((val, idx) => (
							<Grid size={6} key={idx} sx={{ display: "flex", justifyContent: "center" }}>
								<Box
									sx={{
										minWidth: 24,
										height: 20,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										borderRadius: 0.75,
										backgroundColor: val > 0
											? (theme) => alpha(theme.palette.primary.main, 0.06)
											: "transparent",
									}}>
									<Typography
										variant="body2"
										sx={{
											color: val > 0 ? "primary.main" : (theme) => alpha(theme.palette.text.disabled, 0.4),
											fontSize: 11,
											fontWeight: val > 0 ? 700 : 400,
										}}>
										{val}
									</Typography>
								</Box>
							</Grid>
						))}
					</Grid>
				</Grid>
			))}

			{!compact && (
				<Grid size={2} sx={{ display: "flex", justifyContent: "center" }}>
					<Typography
						variant="body2"
						sx={{
							fontSize: 11,
							fontWeight: 700,
						}}>
						{totalReeferAndNonReefer}
					</Typography>
				</Grid>
			)}
		</Grid>
	);
};

const MatrixFooterRow = ({ title, amount }: { title: string, amount: number | string }) => {
	return (
		<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
			<Typography
				variant="caption"
				sx={{
					color: "text.secondary",
					fontSize: 10,
					fontWeight: 700,
					letterSpacing: "0.5px",
				}}>
				{title}:
			</Typography>

			<Typography
				variant="body2"
				sx={{
					color: "text.primary",
					fontSize: 12,
					fontWeight: 700,
				}}>
				{amount}
			</Typography>
		</Stack>
	);
};

export default CallContainersAmount;
